// frontend/src/components/settings/ExactPoResolveDialog.jsx
//
// The PO-match Resolve wizard for an Exact ERP connector parked at RESOLVE —
// the deferred gate that reconciles pre-existing Manexys purchase orders when
// the ERP connects. Each pre-existing PO takes one of four dispositions —
// link / push / keep / discard, where the item and location gates offer link
// / import / skip — so this is its own dialog rather than a mode of
// ResolveGateDialog:
//
//   link    — it IS the suggested Exact order → dedupe into that mirror
//   push    — create it in Exact (it becomes ERP-managed)
//   keep    — leave it in Manexys (stays locally actionable; the default for
//             an unmatched open/partial order)
//   discard — drafts only: delete it (no supplier commitment)
//
// Confirming applies every disposition and clears the gate — the last one, so
// the connector activates.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import DialogErrorBanner from '../common/DialogErrorBanner';

import {
  useExactPoSelection,
  useExactConfirmPos,
} from '../../hooks/useConnectorMutations';
import { translateRequestError } from '../../queries/api';

const ACTION_LABEL_KEYS = {
  link: 'connectors.poActionLink',
  push: 'connectors.poActionPush',
  keep: 'connectors.poActionKeep',
  discard: 'connectors.poActionDiscard',
};

// The dispositions offered for a row, in display order. ``link`` only when a
// mirror was suggested; ``discard`` only for drafts (no supplier commitment).
function availableActions(r) {
  const actions = [];
  if (r.match_mirror_id) actions.push('link');
  actions.push('keep', 'push');
  if (r.is_draft) actions.push('discard');
  return actions;
}

export default function ExactPoResolveDialog({ connectorId, open, onClose, onConfirmed }) {
  const { t } = useTranslation();
  const { data, isPending, error } = useExactPoSelection(connectorId, open);
  const confirmMut = useExactConfirmPos(connectorId);

  // Per-row disposition overrides on top of the backend default_action, keyed
  // by connectorId so re-opening starts clean. Maps po_id → action string.
  const [overrides, setOverrides] = useState({ key: null, map: {} });
  const [confirmError, setConfirmError] = useState(null);

  // Nothing to list until the selection has loaded.
  const rows = useMemo(() => (data ? data.purchase_orders : []), [data]);

  const ov = overrides.key === connectorId ? overrides.map : {};
  const actionOf = (r) => ov[r.po_id] || r.default_action;
  const setAction = (r, action) => setOverrides((prev) => {
    const map = prev.key === connectorId ? { ...prev.map } : {};
    map[r.po_id] = action;
    return { key: connectorId, map };
  });

  const handleConfirm = async () => {
    setConfirmError(null);
    const actions = {};
    for (const r of rows) {
      const action = actionOf(r);
      actions[r.po_id] = action === 'link'
        ? { action: 'link', mirror_id: r.match_mirror_id }
        : { action };
    }
    try {
      const resp = await confirmMut.mutateAsync(actions);
      onConfirmed?.(resp?.summary);
      onClose?.();
    } catch (err) {
      setConfirmError(translateRequestError(err, t, 'connectors.poResolveFailed'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="dialog-form dialog-form--wide">
      <DialogTitle>{t('connectors.poResolveTitle')}</DialogTitle>
      <DialogContent>
        <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {t('connectors.poResolveHelp')}
        </p>

        {isPending ? (
          <div style={{ padding: 24, textAlign: 'center' }}><CircularProgress size={24} /></div>
        ) : error ? (
          <Alert severity="error">{translateRequestError(error, t, 'connectors.resolveLoadFailed')}</Alert>
        ) : rows.length === 0 ? (
          <Alert severity="info">
            {data?.gate_pending === false
              ? t('connectors.poResolveEmptyConfirmed')
              : t('connectors.poResolveEmpty')}
          </Alert>
        ) : (
          <>
            <Alert severity="info">
              {t('connectors.poResolveSummary', {
                matched: data.counts.matched,
                total: data.counts.total,
              })}
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('connectors.poColNumber')}</TableCell>
                  <TableCell>{t('connectors.poColSupplier')}</TableCell>
                  <TableCell>{t('connectors.poColStatus')}</TableCell>
                  <TableCell>{t('connectors.poColMatch')}</TableCell>
                  <TableCell>{t('connectors.poColAction')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.po_id} hover>
                    <TableCell>{r.po_number}</TableCell>
                    <TableCell>{r.supplier_name || '—'}</TableCell>
                    <TableCell>
                      {/* Reuse the grid's canonical PO-status labels
                          (purchaseOrders.status_*) so wording stays consistent
                          and already-translated in every locale. */}
                      <Chip size="small" variant="outlined" label={t(`purchaseOrders.status_${r.status}`)} />
                    </TableCell>
                    <TableCell>
                      {r.match_mirror_id ? (
                        <Chip
                          size="small"
                          color="info"
                          label={t('connectors.poMatchesExact', { number: r.match_exact_po_number })}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={actionOf(r)}
                        onChange={(_e, v) => v && setAction(r, v)}
                      >
                        {availableActions(r).map((a) => (
                          <ToggleButton
                            key={a}
                            value={a}
                            color={a === 'discard' ? 'error' : 'primary'}
                          >
                            {t(ACTION_LABEL_KEYS[a])}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <DialogErrorBanner error={confirmError} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirmMut.isPending}>
          {t('btn.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isPending || rows.length === 0}
          loading={confirmMut.isPending}
        >
          {t('connectors.poResolveConfirm', { count: rows.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
